import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { LegalLayout, type LegalSection } from "@/components/calv/LegalLayout";

const CREATED = "May 7, 2026";
const UPDATED = "May 7, 2026";
const NEXT_REVIEW = "November 7, 2026";

const sections: LegalSection[] = [
  {
    id: "hero-intro",
    num: "01",
    title: "Accessibility isn't checked off, it's continuously maintained",
    content: (
      <>
        <p>
          This declaration honestly documents how accessible calvarius.com currently is — what
          works, what works with limitations, and where we're improving. Self-assessment
          according to WCAG 2.1 Level AA, in line with the German Accessibility Strengthening
          Act (BFSG).
        </p>
        <p>
          <strong>Status:</strong> {CREATED} · <strong>Standard:</strong> WCAG 2.1 Level AA ·{" "}
          <strong>Method:</strong> Self-assessment
        </p>
      </>
    ),
  },
  {
    id: "stance",
    num: "02",
    title: "What accessibility concretely means to us",
    content: (
      <>
        <p>
          Calvarius operates this website with the conviction that digital substance must also
          be accessible. Not because the law requires it from June 2025, but because a site
          that excludes parts of its visitors undermines its own economic claim. Anyone who
          says "we improve online performance" but keeps people with visual impairments,
          motor disabilities, or cognitive differences away from their own site has a
          credibility problem.
        </p>
        <p>
          We deliberately do <strong>not use overlay tools</strong>. These tools are not recommended by the majority of people
          with disabilities, by accessibility experts, or by disability associations, because
          they don't solve the problems in the code but overlay them — and in many cases
          even complicate use with actual assistive technologies like screen readers (NVDA,
          JAWS, VoiceOver). Instead, we build accessibility structurally in the right place:
          in the code, in the design, in the heading hierarchy, in keyboard navigation, in
          contrasts, in alternative texts.
        </p>
        <p>
          This declaration is self-assessment — we examine the site ourselves, honestly
          document where gaps exist, and work on improvements. External confirmation by a
          certified auditor (BIK BITV or comparable) is planned for later. Until then: what's
          stated here is honestly assessed — if something doesn't work, we say so.
        </p>
      </>
    ),
  },
  {
    id: "compliance",
    num: "03",
    title: "Compliance status",
    content: (
      <>
        <p>
          Based on self-assessment by the operator, the calvarius.com website is{" "}
          <strong>partially compliant</strong> with the requirements of the German
          Barrier-Free Information Technology Ordinance (BITV 2.0) and WCAG 2.1 Level AA.
        </p>
        <p>
          The assessment "partially compliant" was chosen because while the vast majority of
          WCAG success criteria are met, individual areas still show need for improvement.
          The concrete limitations are documented in Section 04.
        </p>
        <p>Concretely:</p>
        <ul>
          <li>Structural code basis (heading hierarchy, semantics, ARIA roles) is compliant</li>
          <li>Keyboard navigation works on all routes</li>
          <li>Color contrasts have been checked according to WCAG AA requirements</li>
          <li>All images have alternative texts</li>
          <li>Forms have labels and error messages</li>
          <li>The site is operable with screen readers (NVDA, VoiceOver tested)</li>
          <li>Scaling up to 200% works without layout breaks</li>
        </ul>
      </>
    ),
  },
  {
    id: "limitations",
    num: "04",
    title: "Known limitations",
    content: (
      <>
        <p>
          <strong>Partially accessible:</strong>
        </p>
        <ul>
          <li>
            <strong>Animations on the homepage (hero area)</strong> — Individual animations
            fade in the main heading. People with the browser setting "prefers-reduced-motion"
            should see animations automatically reduced; this has not yet been comprehensively
            tested. WCAG reference: Success criterion 2.3.3 (Animation from Interactions).
          </li>
          <li>
            <strong>Embeddings of external content</strong> — Insofar as we embed external
            tools (e.g., maps, video players), we cannot guarantee their accessibility. No
            external embeddings are currently active. Should this change, the list will be
            supplemented here.
          </li>
          <li>
            <strong>PDF documents (if applicable)</strong> — If Calvarius offers PDF documents
            for download in the future, these must be independently checked for accessibility.
            No PDF documents are currently embedded.
          </li>
        </ul>
        <p>
          <strong>Tested and found compliant:</strong>
        </p>
        <ul>
          <li>The entire navigation (header, footer, mega menu)</li>
          <li>All service detail pages (performance marketing, SEO, tracking, etc.)</li>
          <li>All performance marketing sub-pages (Google Ads, Meta Ads, etc.)</li>
          <li>The tool detail pages (Delta, picazzoo, GonzalAds)</li>
          <li>The contact form</li>
          <li>The legal pages (imprint, privacy policy)</li>
        </ul>
      </>
    ),
  },
  {
    id: "report",
    num: "05",
    title: "Creation of the report",
    content: (
      <>
        <p>
          <strong>Creation date:</strong> {CREATED}
        </p>
        <p>
          <strong>Version tested:</strong> State of the live version on the creation date.
        </p>
        <p>
          <strong>Method:</strong> Self-assessment by the operator Calvarius. Tools used:
        </p>
        <ul>
          <li>Manual testing with keyboard navigation (no mouse use)</li>
          <li>Screen reader test with NVDA (Windows) and VoiceOver (macOS)</li>
          <li>Automated testing with axe DevTools (Chrome Extension)</li>
          <li>Lighthouse Accessibility Audit (Chrome DevTools)</li>
          <li>WAVE Web Accessibility Evaluation Tool</li>
          <li>Manual contrast check with WebAIM Contrast Checker</li>
          <li>Scaling test up to 200% in Chrome, Firefox, Safari</li>
        </ul>
        <p>
          <strong>Success criteria tested:</strong> All 50 criteria of WCAG 2.1 Level A and
          Level AA.
        </p>
        <p>
          <strong>What was not tested:</strong> External tools (if integrated later), PDF
          documents (currently none present), third-party scripts (currently none integrated
          except necessary web analytics).
        </p>
        <p>
          <strong>External confirmation:</strong> None to date. External verification by a
          certified body (e.g., BIK BITV) is planned for Q4 2026.
        </p>
      </>
    ),
  },
  {
    id: "feedback",
    num: "06",
    title: "Feedback mechanism",
    content: (
      <>
        <p>
          Should you find content or functions on calvarius.com that are not accessible to
          you, we welcome your feedback. We take every report seriously and usually respond
          within five business days.
        </p>
        <p>
          <strong>Contact for accessibility matters:</strong>
        </p>
        <p>
          Email: <a href="mailto:info@calvarius.com">info@calvarius.com</a>
          <br />
          Please start the subject line with "Accessibility:" so your inquiry goes directly
          to the responsible person.
        </p>
        <p>Please indicate in your report:</p>
        <ul>
          <li>Which page is affected (URL or description)</li>
          <li>Which assistive technology you use (if relevant)</li>
          <li>Which barrier specifically occurred</li>
          <li>If possible: browser and operating system</li>
        </ul>
        <p>
          We use your information exclusively to address the reported barrier and may, with
          your permission, contact you with follow-up questions.
        </p>
      </>
    ),
  },
  {
    id: "conciliation",
    num: "07",
    title: "Conciliation procedure",
    content: (
      <>
        <p>
          If you are not satisfied with our response or if we do not respond within four
          weeks, you can initiate the conciliation procedure according to § 16 of the German
          Disability Equality Act (BGG) at the BGG Conciliation Body. The procedure is free
          of charge for you and no legal representation is required.
        </p>
        <p>
          <strong>Contact of the BGG Conciliation Body:</strong>
        </p>
        <p>
          Conciliation Body under the Disability Equality Act
          <br />
          at the Federal Government Commissioner for the Concerns of Persons with Disabilities
          <br />
          Mauerstraße 53
          <br />
          10117 Berlin, Germany
          <br />
          Phone: +49 30 18 527-2805
          <br />
          Email:{" "}
          <a href="mailto:info@schlichtungsstelle-bgg.de">info@schlichtungsstelle-bgg.de</a>
          <br />
          Website:{" "}
          <a href="https://www.schlichtungsstelle-bgg.de" target="_blank" rel="noreferrer">
            www.schlichtungsstelle-bgg.de
          </a>
        </p>
      </>
    ),
  },
  {
    id: "status",
    num: "08",
    title: "Last update",
    content: (
      <>
        <p>
          <strong>Created on:</strong> {CREATED}
          <br />
          <strong>Last updated:</strong> {UPDATED}
          <br />
          <strong>Next scheduled review:</strong> {NEXT_REVIEW}
        </p>
        <p>
          This declaration is reviewed at least once a year and reassessed after every
          substantial change to the website.
        </p>
      </>
    ),
  },
];

export const Route = createFileRoute("/en/accessibility")({
  head: () => {
    const h = pageHead("accessibility", "en");
    return { meta: h.meta, links: h.links };
  },
  component: () => (
    <LegalLayout
      eyebrow="A11y"
      title="Accessibility Statement"
      meta={`Status: ${CREATED} · WCAG 2.1 Level AA · Self-assessment`}
      crumbLabel="Accessibility"
      sections={sections}
      homeHref="/en"
      homeLabel="Home"
      tocLabel="Contents"
    />
  ),
});
